import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { Shield, Users, Search, Loader2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch { return '—'; }
};

const AdminPage = () => {
  const { user, token } = useAuth();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!user?.is_admin) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setRows(r.data || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Échec du chargement des utilisateurs");
      }
    })();
    return () => { cancelled = true; };
  }, [user, token]);

  const filtered = useMemo(() => {
    if (!rows) return null;
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(u =>
      (u.username || '').toLowerCase().includes(needle) ||
      (u.email || '').toLowerCase().includes(needle)
    );
  }, [rows, q]);

  // ── Route guard (after hooks to comply with rules-of-hooks) ──
  if (user && !user.is_admin) return <Navigate to="/dashboard" replace />;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6" data-testid="admin-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-white mb-1 flex items-center gap-3">
            <Shield className="w-7 h-7 text-cap-emerald" />
            Administration
          </h1>
          <p className="text-cap-text-secondary text-sm">
            Gestion des utilisateurs de CAP OS
          </p>
        </div>
        {rows && (
          <div className="flex items-center gap-2 text-sm text-cap-text-secondary bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2">
            <Users className="w-4 h-4 text-cap-emerald" />
            <span><span className="text-white font-semibold">{rows.length}</span> utilisateurs</span>
          </div>
        )}
      </div>

      {/* Card */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-5 lg:p-6" data-testid="admin-card">
        {/* Search */}
        <div className="relative max-w-sm mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cap-text-muted" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher par username ou email…"
            className="pl-9 bg-cap-bg border-white/10 text-white placeholder:text-cap-text-muted"
            data-testid="admin-search-input"
          />
        </div>

        {error && (
          <div className="rounded-lg border border-cap-error/30 bg-cap-error/5 p-4 flex items-start gap-3" data-testid="admin-error">
            <AlertTriangle className="w-5 h-5 text-cap-error flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-cap-error font-medium">Erreur</p>
              <p className="text-cap-text-secondary text-sm mt-1">{error}</p>
            </div>
          </div>
        )}

        {!error && !filtered && (
          <div className="flex items-center justify-center py-16 text-cap-text-muted text-sm">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement des utilisateurs…
          </div>
        )}

        {filtered && (
          <div className="overflow-x-auto -mx-2" data-testid="admin-users-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-cap-text-muted text-xs uppercase tracking-wide border-b border-white/5">
                  <th className="text-left px-3 py-3 font-medium">Username</th>
                  <th className="text-left px-3 py-3 font-medium">Email</th>
                  <th className="text-left px-3 py-3 font-medium">Inscription</th>
                  <th className="text-left px-3 py-3 font-medium">Dernière connexion</th>
                  <th className="text-right px-3 py-3 font-medium">Niveau (XP)</th>
                  <th className="text-right px-3 py-3 font-medium">CAP Score</th>
                  <th className="text-center px-3 py-3 font-medium">Admin</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={7} className="text-center text-cap-text-muted py-10">Aucun utilisateur trouvé</td></tr>
                ) : filtered.map(u => (
                  <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`admin-user-row-${u.id}`}>
                    <td className="px-3 py-3 text-white font-medium">{u.username || '—'}</td>
                    <td className="px-3 py-3 text-cap-text-secondary">{u.email}</td>
                    <td className="px-3 py-3 text-cap-text-secondary">{fmtDate(u.created_at)}</td>
                    <td className="px-3 py-3 text-cap-text-secondary">{fmtDate(u.last_login)}</td>
                    <td className="px-3 py-3 text-right">
                      <span className="text-white font-semibold">N{u.level}</span>
                      <span className="text-cap-text-muted text-xs ml-1">({u.xp} XP)</span>
                    </td>
                    <td className="px-3 py-3 text-right text-cap-emerald font-mono font-semibold">{u.cap_score ?? 0}</td>
                    <td className="px-3 py-3 text-center">
                      {u.is_admin && (
                        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-cap-emerald/30 bg-cap-emerald/10 text-cap-emerald font-medium">
                          <Shield className="w-3 h-3" /> admin
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AdminPage;
